import { useState, useEffect, useRef } from "react";
import { useAzureFileDownload } from "./useAzureFileDownload";

const MAX_POLL_ATTEMPTS = 30;

export const useAzureVideoDownload = (
	uploadType: string,
	defaultUrl?: string,
	uuid?: string | null
) => {
	const [url, setUrl] = useState<string | undefined>(defaultUrl);
	const [processingStatus, setProcessingStatus] = useState<string>("");
	const [thumbnail, setThumbnail] = useState<string>("");
	const [timerReference, setTimerReference] = useState<number | null>(null);
	const pollCountRef = useRef(0);
	const { data, error, download } = useAzureFileDownload(uploadType, uuid);

	// Reset state when UUID changes
	useEffect(() => {
		setUrl(defaultUrl);
		setProcessingStatus("");
		setThumbnail("");
		pollCountRef.current = 0;

		if (timerReference) {
			window.clearTimeout(timerReference);
			setTimerReference(null);
		}
	}, [uuid]);

	// Cleanup pending timer on unmount
	useEffect(() => {
		return () => {
			if (timerReference) {
				window.clearTimeout(timerReference);
			}
		};
	}, [timerReference]);

	useEffect(() => {
		if (!data) return;

		if (data["processing_status"])
			setProcessingStatus(data["processing_status"]);
		if (data["thumbnail"]) setThumbnail(data["thumbnail"]);

		if (data["playback_uri"]) {
			setUrl(data["playback_uri"]);
			if (timerReference) {
				window.clearTimeout(timerReference);
				setTimerReference(null);
			}
		} else if (
			uuid &&
			data["processing_status"] !== "ReadyForPlayback" &&
			pollCountRef.current < MAX_POLL_ATTEMPTS
		) {
			// Video exists but isn't playable yet — poll until ready
			if (timerReference) {
				window.clearTimeout(timerReference);
			}
			pollCountRef.current += 1;
			setTimerReference(
				window.setTimeout(() => {
					download();
				}, 4000)
			);
		}
	}, [data]);

	useEffect(() => {
		if (data) return;

		setUrl(defaultUrl);
	}, [defaultUrl]);

	useEffect(() => {
		//
		// Video hasn't finished processing yet
		//
		if (
			processingStatus === "Processing" &&
			pollCountRef.current < MAX_POLL_ATTEMPTS
		) {
			setProcessingStatus("");

			if (timerReference) {
				window.clearTimeout(timerReference);
			}

			pollCountRef.current += 1;
			setTimerReference(
				window.setTimeout(() => {
					download();
				}, 4000)
			);
		}
	}, [processingStatus]);

	return {
		url,
		processingStatus,
		thumbnail,
		error
	};
};
